import React, { createContext, useContext, useLayoutEffect } from "react";
import type { ComponentType, ReactElement, ReactNode } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";
import type { AnyRoute } from "@tanstack/react-router";
import type { PageRouteData } from "@/shared/lib/router/router";

type PageComponent = ComponentType & {
  getLayout?: (page: ReactElement) => ReactNode;
  flush?: boolean;
};
export type PageModule = { default: PageComponent };

/** Permite que a página ativa informe ao shell se é "flush" (sem rail/topbar). */
export const FlushContext = createContext<(flush: boolean) => void>(() => undefined);

const pageModules = import.meta.glob<PageModule>([
  "/src/pages/**/*.tsx",
  "!/src/pages/_*.tsx",
  "!/src/pages/404.tsx",
]);

export const loadNotFoundPage = () => import("@/pages/404");

/** "/src/pages/project/[projectId].tsx" → "/project/[projectId]" (formato do Next). */
function toNextPath(file: string): string {
  const path = file.replace(/^\/src\/pages/, "").replace(/\.tsx$/, "").replace(/\/index$/, "");
  return path === "" ? "/" : path;
}

/** "/admin/entregas/[[...slug]]" → "/admin/entregas/$"; "[id]" → "$id". */
function toRoutePath(nextPath: string): { path: string; splatParam?: string } {
  let splatParam: string | undefined;
  const path = nextPath.replace(/\[\[?\.\.\.([^\]]+)\]\]?/, (_, name: string) => {
    splatParam = name;
    return "$";
  });
  return { path: path.replace(/\[([^\]]+)\]/g, "$$$1"), splatParam };
}

function withShell(Page: PageComponent): ComponentType {
  const getLayout = Page.getLayout ?? ((page: ReactElement) => page);
  return function PageRoute() {
    const setFlush = useContext(FlushContext);
    useLayoutEffect(() => setFlush(Boolean(Page.flush)), [setFlush]);
    return <>{getLayout(<Page />)}</>;
  };
}

/** Rota de uma página no formato do Next ("/tenants/[id]"), com o shell dela. */
export function createPageRoute(parent: AnyRoute, nextPath: string, load: () => Promise<PageModule>): AnyRoute {
  const { path, splatParam } = toRoutePath(nextPath);
  const staticData: PageRouteData = { nextPath, splatParam };
  return createRoute({
    getParentRoute: () => parent,
    path,
    staticData,
    component: lazyRouteComponent(async () => ({ default: withShell((await load()).default) })),
  });
}

export function createPageRoutes(parent: AnyRoute): AnyRoute[] {
  return Object.entries(pageModules).map(([file, load]) => createPageRoute(parent, toNextPath(file), load));
}

/** Segmentos de primeiro nível das páginas ("/dashboard" → "dashboard"): nunca são slug de organização. */
export const pageRoots: ReadonlySet<string> = new Set(
  Object.keys(pageModules)
    .map((file) => toNextPath(file).split("/")[1] ?? "")
    .filter(Boolean),
);
